import type { Market } from '../context/MarketContext';

export interface WebDevPlan {
  name: string;
  price: string;
  period: string;
  description: string;
  includesLabel: string;
  features: string[];
  idealForLabel: string;
  idealFor: string;
  note: string;
  highlighted: boolean;
  badge: string;
}

export interface WebDevHostingItem {
  title: string;
  price: string;
  period: string;
  description: string;
  note?: string;
  includes?: string[];
}

export interface WebDevOptionalService {
  service: string;
  price: string;
}

export interface WebDevCta {
  title: string;
  description: string;
  bundleLabel: string;
  totalLabel: string;
  total: string;
  lineItems: string[];
  footerNote: string;
  quoteNote: string;
}

export interface WebDevPricing {
  packagesFrom: string;
  plans: WebDevPlan[];
  domainHostingDisclaimer: string;
  domain: WebDevHostingItem;
  hosting: WebDevHostingItem;
  maintenance: WebDevHostingItem;
  optionalServices: WebDevOptionalService[];
  optionalDisclaimer: string;
  cta: WebDevCta;
}

const usPricing: WebDevPricing = {
  packagesFrom: 'Packages from $499',
  plans: [
    {
      name: 'Starter',
      price: '$499',
      period: 'one-time',
      description: 'A professional online presence for businesses that need a simple, straightforward website.',
      includesLabel: 'Includes',
      features: [
        'Up to 5 pages',
        'Professional responsive design',
        'Mobile-friendly layout',
        'Contact form',
        'Basic SEO setup',
        'Social media links',
        'Google Maps',
        'SSL/HTTPS',
        'Website deployment',
        '1 round of revisions',
      ],
      idealForLabel: 'Best for',
      idealFor: 'Small businesses that need a professional website with essential company information.',
      note: '',
      highlighted: false,
      badge: '',
    },
    {
      name: 'Professional',
      price: '$799',
      period: 'one-time',
      description: 'A comprehensive business website designed to showcase your company, capabilities, and services.',
      includesLabel: 'Includes',
      features: [
        'Up to 8 pages',
        'Custom website design',
        'Responsive/mobile-friendly design',
        'Services & capabilities sections',
        'Equipment / facilities section',
        'Industries served',
        'Image gallery',
        'Projects / portfolio section',
        'Request for Quote / inquiry form',
        'Contact form',
        'Google Maps',
        'Social media integration',
        'Basic SEO setup',
        'SSL/HTTPS',
        'Website deployment',
        '2 rounds of revisions',
      ],
      idealForLabel: 'Best for',
      idealFor: 'Machine shops, manufacturers, fabricators, and growing businesses.',
      note: '',
      highlighted: true,
      badge: 'Our Recommended Package',
    },
    {
      name: 'Custom',
      price: 'Starting at $1,199',
      period: '',
      description: 'For businesses that require additional pages, functionality, or a more customized website.',
      includesLabel: 'May include',
      features: [
        '10+ pages',
        'Detailed service/product pages',
        'Advanced RFQ forms',
        'Product/service catalogs',
        'Additional integrations',
        'Custom functionality',
        'Additional SEO requirements',
        'Business email setup',
        'Ongoing website maintenance',
      ],
      idealForLabel: '',
      idealFor: '',
      note: 'A detailed quotation will be provided based on the specific requirements.',
      highlighted: false,
      badge: '',
    },
  ],
  domainHostingDisclaimer:
    'Domain, hosting, and optional maintenance are billed separately from website development unless included in a package.',
  domain: {
    title: 'Domain Registration',
    price: '$15',
    period: '/ year',
    description: 'We can help you select, register, and configure a professional domain name for your business.',
    note: 'Actual price may vary depending on the domain extension selected.',
  },
  hosting: {
    title: 'Website Hosting',
    price: '$99',
    period: '/ year',
    description: '',
    includes: [
      'Website hosting',
      'SSL/HTTPS',
      'Website deployment',
      'Domain connection',
      'Basic technical support',
    ],
  },
  maintenance: {
    title: 'Website Maintenance',
    price: '$299',
    period: '/ year',
    description:
      'Optional ongoing maintenance and support, including minor content updates, technical assistance, and routine website maintenance.',
  },
  optionalServices: [
    { service: 'Additional standard page', price: '$50/page' },
    { service: 'Additional revision round', price: '$75' },
    { service: 'RFQ / advanced inquiry form', price: '$100' },
    { service: 'Business email setup', price: '$50' },
    { service: 'Basic logo design', price: '$100' },
    { service: 'Content writing', price: 'Quoted separately' },
    { service: 'SEO services', price: 'Quoted separately' },
    { service: 'Ongoing website updates', price: '$29/month' },
  ],
  optionalDisclaimer:
    'Third-party services, paid plugins, premium images, email subscriptions, and other external services are billed separately where applicable.',
  cta: {
    title: 'Get started for $799',
    description:
      'For most small and mid-sized machine shops, our Professional Website Package at $799 provides everything needed to establish a strong, professional online presence without the cost of a large agency.',
    bundleLabel: 'Website + Domain + Hosting',
    totalLabel: 'First-year starting cost',
    total: '$913',
    lineItems: [
      '$799 — Website development',
      '$15 — Domain registration',
      '$99 — First-year hosting',
    ],
    footerNote: 'Optional maintenance is available at $299/year.',
    quoteNote: 'A final quotation will be provided after reviewing your requirements.',
  },
};

const indiaPricing: WebDevPricing = {
  packagesFrom: 'Packages from ₹14,999',
  plans: [
    {
      name: 'Starter',
      price: '₹14,999',
      period: 'one-time',
      description: 'A professional online presence for businesses that need a simple, clean website.',
      includesLabel: 'Includes',
      features: [
        'Up to 5 pages',
        'Responsive website design',
        'Mobile-friendly layout',
        'Contact form',
        'Basic SEO setup',
        'Social media links',
        'Google Maps',
        'SSL/HTTPS',
        'Website deployment',
        '1 round of revisions',
      ],
      idealForLabel: 'Ideal for',
      idealFor: 'Small businesses, professionals and startups.',
      note: '',
      highlighted: false,
      badge: '',
    },
    {
      name: 'Professional',
      price: '₹24,999',
      period: 'one-time',
      description: 'A complete business website designed to showcase your company, products, services and capabilities.',
      includesLabel: 'Includes',
      features: [
        'Up to 8 pages',
        'Professional custom design',
        'Mobile-responsive layout',
        'About Us',
        'Products / Services',
        'Capabilities',
        'Equipment / Facilities',
        'Industries Served',
        'Gallery / Projects',
        'Contact Us',
        'Enquiry / RFQ form',
        'Google Maps',
        'Social media integration',
        'Basic SEO setup',
        'SSL/HTTPS',
        'Website deployment',
        '2 rounds of revisions',
      ],
      idealForLabel: 'Ideal for',
      idealFor: 'Manufacturers, machine shops, fabricators, service companies and growing businesses.',
      note: '',
      highlighted: true,
      badge: 'Recommended',
    },
    {
      name: 'Custom',
      price: 'Starting at ₹39,999',
      period: '',
      description: 'For businesses requiring additional pages, functionality or a more customized website.',
      includesLabel: 'May include',
      features: [
        '10+ pages',
        'Detailed service/product pages',
        'Advanced enquiry / RFQ forms',
        'Product or service catalog',
        'Custom functionality',
        'Additional integrations',
        'Advanced SEO requirements',
        'Business email setup',
        'Ongoing maintenance',
      ],
      idealForLabel: '',
      idealFor: '',
      note: 'A customized quotation will be provided based on the requirements.',
      highlighted: false,
      badge: '',
    },
  ],
  domainHostingDisclaimer:
    'Domain, hosting, and optional maintenance are billed separately from website development unless included in a package.',
  domain: {
    title: 'Domain Registration',
    price: '₹999',
    period: '/ year',
    description: 'We can help you select, register and configure a professional domain name.',
    note: 'Actual pricing may vary depending on the domain extension.',
  },
  hosting: {
    title: 'Website Hosting',
    price: '₹2,999',
    period: '/ year',
    description: '',
    includes: [
      'Website hosting',
      'SSL/HTTPS',
      'Website deployment',
      'Domain connection',
      'Basic technical support',
    ],
  },
  maintenance: {
    title: 'Website Maintenance',
    price: '₹9,999',
    period: '/ year',
    description:
      'Optional ongoing maintenance and support, including minor content updates, technical assistance and routine website maintenance.',
  },
  optionalServices: [
    { service: 'Additional standard page', price: '₹1,000/page' },
    { service: 'Additional revision round', price: '₹1,500' },
    { service: 'Advanced RFQ / enquiry form', price: '₹2,000' },
    { service: 'Business email setup', price: '₹1,500' },
    { service: 'Basic logo design', price: '₹2,500' },
    { service: 'Content writing', price: 'Quoted separately' },
    { service: 'SEO services', price: 'Quoted separately' },
    { service: 'Ongoing website updates', price: '₹999/month' },
  ],
  optionalDisclaimer:
    'Third-party services, premium images, paid plugins, email subscriptions and other external services are billed separately where applicable.',
  cta: {
    title: 'Complete website solution',
    description:
      'Get online with our Starter package plus domain and first-year hosting. For a more comprehensive website, our Professional Package at ₹24,999 offers additional pages and features to showcase your business more effectively.',
    bundleLabel: 'Website + Domain + Hosting + Support',
    totalLabel: 'Starting at',
    total: '₹18,997',
    lineItems: [
      '₹14,999 — Starter website',
      '₹999 — Domain',
      '₹2,999 — First-year hosting',
    ],
    footerNote: 'One provider. One complete solution.',
    quoteNote: "Let's build your online presence.",
  },
};

export const getWebDevelopmentPricing = (market: Market): WebDevPricing =>
  market === 'IN' ? indiaPricing : usPricing;
